<template>
  <span class="fish-info">
    <button
      ref="gatilho"
      class="fish-info__gatilho"
      :class="{ 'brilho-unico': primeiraVez }"
      type="button"
      :aria-label="`Sobre: ${titulo}`"
      :aria-expanded="aberto ? 'true' : 'false'"
      @click.stop="alternar"
      @keydown.esc="fechar"
    >
      i
    </button>

    <Transition name="balao-i">
      <div
        v-if="aberto"
        ref="balao"
        class="fish-info__balao"
        :class="[`fish-info__balao--${lado}`]"
        role="dialog"
        :aria-label="titulo"
      >
        <p class="fish-info__titulo t-rotulo">{{ titulo }}</p>
        <div class="fish-info__corpo t-corpo"><slot>{{ texto }}</slot></div>
        <span class="fish-info__bico" aria-hidden="true" />
      </div>
    </Transition>
  </span>
</template>

<script>
/** Onde o "i" já brilhou. Vive no `localStorage` para valer entre sessões (regra 5). */
const CHAVE = 'fishbay:i-visto';

function jaVistos() {
  try {
    return new Set(JSON.parse(localStorage.getItem(CHAVE) || '[]'));
  } catch {
    // `localStorage` pode lançar (janela anônima, cookies bloqueados). Um "i" que brilha de novo é
    // um incômodo pequeno; uma tela que não renderiza por causa disso não é.
    return new Set();
  }
}

/**
 * 7.29 — o componente "i" (D30).
 *
 * > Não são tooltips espalhadas conforme a necessidade aparece. É **um componente, cinco regras e
 * > uma lista** — senão viram trinta explicações escritas em trinta estilos.
 *
 * ## As cinco regras, e onde cada uma está no código
 *
 * **1. Diz o que faz por você, em número. Não conta história.** *"Recarrega +8 de energia por
 * descanso, contra +5 da anterior"* ensina; *"um refúgio aconchegante à beira-mar"* não ensina nada.
 * Isso é conteúdo, então mora em quem usa — mas a `prop` se chama `texto` e não `descricao` de
 * propósito, e este comentário é o lembrete.
 *
 * **2. O "i" nunca esconde informação necessária.** Se o jogador precisa daquilo para agir, vai na
 * tela. **É a regra mais importante**, porque o "i" é um lugar tentador para esconder o que a tela
 * não soube dizer — e aí ele deixa de ser ajuda e vira desculpa.
 *
 * **3. Abre no lugar.** Um balão sobre a tela, nunca uma navegação. Fecha com um toque fora. Ler a
 * explicação não pode custar perder o que se estava fazendo.
 *
 * **4. É alcançável.** Alvo de 44px (por `::after`, para o desenho continuar pequeno), foco
 * visível, e funciona por teclado (D11, UI-29).
 *
 * **5. Pisca uma vez, suave, na primeira vez** — e nunca mais. É o D0: chamar atenção sem cobrar.
 *
 * ## E o aviso sobre o próprio "i" (D30)
 *
 * > **O "i" é um remendo para opacidade. Onde forem precisos muitos, o desenho está errado.**
 *
 * Se uma tela precisa de três, ela é uma tela que não se explica. A lista dos doze lugares do D30
 * deve **encolher** conforme as telas melhorarem — não crescer. Vale reavaliá-la ao fim de cada
 * tela refeita (7.29b): o que virou óbvio dispensa o "i".
 */
export default {
  name: 'FishInfo',
  props: {
    /** Identifica este "i" para o brilho único. Ex.: `armazem`, `rank-funcionario`. */
    id: { type: String, required: true },
    titulo: { type: String, required: true },
    texto: { type: String, default: '' },
    lado: { type: String, default: 'cima', validator: (v) => ['cima', 'baixo'].includes(v) },
  },
  data() {
    return { aberto: false, primeiraVez: false };
  },
  mounted() {
    // Regra 5: brilha uma vez, e o registro é gravado JÁ — não ao fechar o balão. Se dependesse de
    // o jogador abrir, quem ignorasse o "i" veria o brilho para sempre, que é o oposto de "uma vez".
    if (!jaVistos().has(this.id)) {
      this.primeiraVez = true;
      this.registrar();
      setTimeout(() => {
        this.primeiraVez = false;
      }, 1400);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.fechar);
  },
  methods: {
    registrar() {
      try {
        const vistos = jaVistos();
        vistos.add(this.id);
        localStorage.setItem(CHAVE, JSON.stringify([...vistos]));
      } catch {
        /* sem armazenamento: o brilho repete, e tudo bem */
      }
    },
    alternar() {
      this.aberto = !this.aberto;
      // Regra 3: fecha com um toque fora. O ouvinte só existe enquanto o balão está aberto — um
      // ouvinte permanente no documento por "i" seria doze ouvintes numa tela do D30.
      if (this.aberto) document.addEventListener('click', this.fechar, { once: true });
    },
    fechar() {
      this.aberto = false;
    },
  },
};
</script>

<style scoped>
.fish-info {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
}

/**
 * O gatilho é pequeno e o ALVO é grande (regra 4).
 *
 * 20px de desenho para não empurrar a linha em que ele vive, e 44px de área de toque pelo `::after`
 * invisível. É a mesma técnica do `.alvo-invisivel` do `base.css`: o alvo cresce, o desenho não.
 */
.fish-info__gatilho {
  position: relative;
  width: 20px;
  height: 20px;
  min-width: 0;
  min-height: 0;
  padding: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-family: var(--fonte-display);
  font-size: var(--f-1);
  font-style: italic;
  line-height: 1;

  /* Escuro sobre pergaminho: o "i" vive dentro de um painel de papel, entao ele e a tinta. */
  border: 2px solid var(--latao);
  border-radius: var(--r-circulo);
  background: var(--madeira);
  color: var(--ouro-claro);
}

.fish-info__gatilho::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--alvo-minimo);
  height: var(--alvo-minimo);
  transform: translate(-50%, -50%);
}

@media (hover: hover) {
  .fish-info__gatilho:hover {
    background: var(--madeira);
  }
}

.fish-info__balao {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  /* `--z-balao` (600) é ACIMA de `--z-modal` (500), e é de propósito: um "i" dentro de um modal que
   * abrisse por baixo dele não serviria para nada. */
  z-index: var(--z-balao);

  width: max-content;
  max-width: min(280px, 80vw);
  padding: var(--e-2) var(--e-3);

  background: var(--papel-frio);
  color: var(--tinta);
  border: 3px solid var(--madeira-escura);
  box-shadow: var(--sombra-difusa);
  text-align: left;
  cursor: default;
}

.fish-info__balao--cima {
  bottom: calc(100% + 10px);
}

.fish-info__balao--baixo {
  top: calc(100% + 10px);
}

.fish-info__titulo {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-1);
}

.fish-info__corpo {
  margin: 0;
}

.fish-info__bico {
  position: absolute;
  left: 50%;
  width: 10px;
  height: 10px;
  transform: translateX(-50%) rotate(45deg);
  background: var(--papel-frio);
}

.fish-info__balao--cima .fish-info__bico {
  bottom: -6px;
  border-right: 3px solid var(--madeira-escura);
  border-bottom: 3px solid var(--madeira-escura);
}

.fish-info__balao--baixo .fish-info__bico {
  top: -6px;
  border-left: 3px solid var(--madeira-escura);
  border-top: 3px solid var(--madeira-escura);
}

.balao-i-enter-active,
.balao-i-leave-active {
  transition: opacity var(--t-rapido) var(--curva-suave), transform var(--t-rapido) var(--curva-entrada);
}

.balao-i-enter-from,
.balao-i-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px);
}
</style>
